import { getPdfExcludedColumnIds as getPdfExcludedColumnIdsForSelection } from '@augmentt-dev/ui-components'

/** Optional columns that can be toggled in the single-company compliance audit table. */
export const COLUMN_IDS = {
  STATUS: 'status',
  STATUS_DETAILS: 'statusDetails',
  CATEGORIES: 'categories',
  PORTAL: 'portal',
  RATIONALE: 'rationale',
  /**
   * Customize-menu meta id: toggles both Ref. # and Compliance table columns.
   * Shown in Customize only when an Augmentt default assessment is selected.
   */
  COMPLIANCE: 'compliance',
  ASSESSMENTS: 'assessments',
  LICENSE: 'license',
  SECURE_SCORE: 'secureScore',
  NOTES: 'notes',
}

/** CheckTable dataField for the Compliance text column (assessment name header). */
export const COMPLIANCE_DATA_FIELD = 'compliance'

/** CheckTable dataField for the control reference (Ref. #) column. */
export const COMPLIANCE_REF_DATA_FIELD = 'complianceRef'

/** Table/export fields controlled by the single Customize "Compliance" option. */
export const COMPLIANCE_META_DATA_FIELDS = [COMPLIANCE_REF_DATA_FIELD, COMPLIANCE_DATA_FIELD]

/**
 * Always shown; not listed in the customize menu.
 * Security Check + Status stay visible for every audit view/export.
 */
export const ALWAYS_VISIBLE_DATA_FIELDS = ['formattedName', 'formattedStatus']

/** @deprecated Prefer ALWAYS_VISIBLE_DATA_FIELDS; kept for existing imports. */
export const ALWAYS_VISIBLE_DATA_FIELD = ALWAYS_VISIBLE_DATA_FIELDS[0]

export const NOTES_DISPLAY_MODES = {
  FULL_TEXT: 'fullText',
  STATUS_ONLY: 'statusOnly',
}

/**
 * Maps customize-menu column ids to CheckTable `dataField` values.
 * Security Check and Status are always on and not customizable.
 * Compliance is a meta option handled in getDataFieldsForColumnId.
 */
export const COLUMN_ID_TO_DATA_FIELD = {
  [COLUMN_IDS.STATUS_DETAILS]: 'formattedStatusDetails',
  [COLUMN_IDS.CATEGORIES]: 'category',
  [COLUMN_IDS.PORTAL]: 'portal',
  [COLUMN_IDS.RATIONALE]: 'rationale',
  [COLUMN_IDS.ASSESSMENTS]: 'formattedTemplates',
  [COLUMN_IDS.LICENSE]: 'license',
  [COLUMN_IDS.SECURE_SCORE]: 'msSecureScore.currentScore',
  [COLUMN_IDS.NOTES]: 'notes',
}

/** Resolves a customize-menu id to one or more CheckTable dataFields. */
export const getDataFieldsForColumnId = (columnId) => {
  if (columnId === COLUMN_IDS.COMPLIANCE) {
    return [...COMPLIANCE_META_DATA_FIELDS]
  }

  const field = COLUMN_ID_TO_DATA_FIELD[columnId]
  return field ? [field] : []
}

export const ALL_CUSTOMIZABLE_COLUMN_IDS = [
  COLUMN_IDS.COMPLIANCE,
  COLUMN_IDS.STATUS_DETAILS,
  COLUMN_IDS.CATEGORIES,
  COLUMN_IDS.PORTAL,
  COLUMN_IDS.RATIONALE,
  COLUMN_IDS.ASSESSMENTS,
  COLUMN_IDS.LICENSE,
  COLUMN_IDS.SECURE_SCORE,
  COLUMN_IDS.NOTES,
]

/** Off until the user turns them on — Category is wide enough to crowd the default table. */
export const DEFAULT_HIDDEN_COLUMN_IDS = [COLUMN_IDS.CATEGORIES]

export const DEFAULT_VISIBLE_COLUMN_IDS = ALL_CUSTOMIZABLE_COLUMN_IDS.filter(
  (id) => !DEFAULT_HIDDEN_COLUMN_IDS.includes(id),
)

export const COLUMN_LABEL_MESSAGE_IDS = {
  [COLUMN_IDS.STATUS_DETAILS]: 'COMPLIANCE_AUDIT.STATUS_DETAILS',
  [COLUMN_IDS.CATEGORIES]: 'COMPLIANCE_AUDIT.CATEGORIES',
  [COLUMN_IDS.PORTAL]: 'COMPLIANCE_AUDIT.PORTAL',
  [COLUMN_IDS.RATIONALE]: 'COMPLIANCE_AUDIT.RATIONALE',
  [COLUMN_IDS.COMPLIANCE]: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE',
  [COLUMN_IDS.ASSESSMENTS]: 'COMPLIANCE_AUDIT.TEMPLATES',
  [COLUMN_IDS.LICENSE]: 'COMPLIANCE_AUDIT.M365_LICENSE_REQ',
  [COLUMN_IDS.SECURE_SCORE]: 'COMPLIANCE_AUDIT.SECURE_SCORE',
  [COLUMN_IDS.NOTES]: 'GENERAL.NOTES',
}

export const COLUMN_CONFIG_STORAGE_KEY = 'complianceAudit.columnConfig'

export const createDefaultColumnConfig = () => ({
  visibleColumnIds: [...DEFAULT_VISIBLE_COLUMN_IDS],
  notesDisplayMode: NOTES_DISPLAY_MODES.FULL_TEXT,
})

const getVisibleColumnIdsOrDefault = (columnConfig) => columnConfig?.visibleColumnIds ?? DEFAULT_VISIBLE_COLUMN_IDS

export const getEnabledDataFields = (columnConfig) => {
  const visibleIds = getVisibleColumnIdsOrDefault(columnConfig)

  return [...ALWAYS_VISIBLE_DATA_FIELDS, ...visibleIds.flatMap(getDataFieldsForColumnId)]
}

/** Whether Compliance (Ref. # + Compliance) is checked in Customize. */
export const isComplianceColumnEnabled = (columnConfig) =>
  getVisibleColumnIdsOrDefault(columnConfig).includes(COLUMN_IDS.COMPLIANCE)

/**
 * Selected column ids, minus Compliance when it isn't available for the current scope. Export
 * config sets showComplianceColumn=false even if the id remains in stored visibleColumnIds.
 */
const getBudgetEligibleColumnIds = (columnConfig) => {
  const visibleIds = getVisibleColumnIdsOrDefault(columnConfig)

  return columnConfig?.showComplianceColumn === false
    ? visibleIds.filter((columnId) => columnId !== COLUMN_IDS.COMPLIANCE)
    : visibleIds
}

/** Total PDF/table columns including always-visible Security Check and Status. */
export const getSelectedColumnCount = (columnConfig) =>
  ALWAYS_VISIBLE_DATA_FIELDS.length +
  getBudgetEligibleColumnIds(columnConfig).reduce((sum, columnId) => sum + getDataFieldsForColumnId(columnId).length, 0)

/**
 * Selected columns that will be left out of the PDF: Portal and Notes always, plus any remaining
 * columns cut to fit the PDF's fixed column budget (see getPdfVisibleColumnIds in ui-components).
 */
export const getPdfExcludedColumnIds = (columnConfig) =>
  getPdfExcludedColumnIdsForSelection(getBudgetEligibleColumnIds(columnConfig), ALWAYS_VISIBLE_DATA_FIELDS.length)

/** Bakes an explicit true/false for every currently customizable column. */
const toColumnVisibility = (visibleColumnIds) =>
  Object.fromEntries(ALL_CUSTOMIZABLE_COLUMN_IDS.map((id) => [id, visibleColumnIds.includes(id)]))

export const saveColumnConfigToStorage = (columnConfig) => {
  try {
    localStorage.setItem(
      COLUMN_CONFIG_STORAGE_KEY,
      JSON.stringify({
        columnVisibility: toColumnVisibility(columnConfig.visibleColumnIds ?? []),
        notesDisplayMode: columnConfig.notesDisplayMode,
      }),
    )
  } catch {
    // Ignore quota / private-mode failures; in-memory state still applies for the session.
  }
}

export const loadColumnConfigFromStorage = () => {
  try {
    const raw = localStorage.getItem(COLUMN_CONFIG_STORAGE_KEY)
    if (!raw) {
      return createDefaultColumnConfig()
    }

    const parsed = JSON.parse(raw)
    const notesDisplayMode = Object.values(NOTES_DISPLAY_MODES).includes(parsed?.notesDisplayMode)
      ? parsed.notesDisplayMode
      : NOTES_DISPLAY_MODES.FULL_TEXT

    // Preferences saved before per-column visibility existed stored a plain array of visible ids
    // instead. Migrate that once, baking an explicit true/false for every column that exists now, and
    // persist it immediately — no Customize interaction required. From then on a column absent from
    // columnVisibility (including any introduced later) is undecided: default-hidden columns stay
    // off, everything else surfaces.
    const isLegacyArrayFormat = parsed?.columnVisibility == null && Array.isArray(parsed?.visibleColumnIds)
    const columnVisibility = isLegacyArrayFormat
      ? toColumnVisibility(parsed.visibleColumnIds.filter((id) => ALL_CUSTOMIZABLE_COLUMN_IDS.includes(id)))
      : (parsed?.columnVisibility ?? {})

    const config = {
      visibleColumnIds: ALL_CUSTOMIZABLE_COLUMN_IDS.filter(
        (id) => columnVisibility[id] ?? !DEFAULT_HIDDEN_COLUMN_IDS.includes(id),
      ),
      notesDisplayMode,
    }

    if (isLegacyArrayFormat) {
      saveColumnConfigToStorage(config)
    }

    return config
  } catch {
    return createDefaultColumnConfig()
  }
}

export const getLatestNote = (notes) => {
  if (!Array.isArray(notes) || !notes.length) {
    return null
  }

  return [...notes].sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))[0]
}

export const formatNotesCell = (notes, notesDisplayMode, formatStatusLabel) => {
  const latest = getLatestNote(notes)
  if (!latest) {
    return null
  }

  if (notesDisplayMode === NOTES_DISPLAY_MODES.STATUS_ONLY) {
    return formatStatusLabel(latest) || null
  }

  return latest.note ?? null
}
